import { CloseOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Input,
  Modal,
  Popover,
  Progress,
  Radio,
  Tooltip
} from 'antd';
import { find } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  putRiskScoreAcceptance,
  updateCRSRiskScoreHistory
} from '../../../../redux/actions/crs/crsRiskDetail';
import { getInDecimal } from '../../../../services/util';
import { hasResource } from '../../../../util';
import { crsResources } from '../../../../util/GlobalConstant';
import { normalizeAlertScore } from '../../../../util/normalization';
import { CRS_CONSTANTS } from '../../../constants/crsContants';
import ScoreHistoryTable from '../../components/ScoreHistoryTable';
import { segmentMapping } from '../ClusterInformation';
import styles from './index.scss';
import {
  isValidReason,
  ScoringPanelCorporateConfig,
  ScoringPanelIndividualConfig
} from './util';

const { GET_DATA } = CRS_CONSTANTS;

const getColor = (riskRating) => {
  switch (riskRating) {
    case 'High':
      return '#DF6057';
    case 'Medium':
      return '#E29840';
    case 'Low':
      return '#71C969';
    default:
      return '#71C969';
  }
};

const convertYearsToCamelCase = (string) => {
  if (string && string.includes('year')) {
    const formattedString = string.replace('year', 'Year');
    return formattedString;
  }

  return string;
};

export const ScoreBar = ({
  score,
  riskRating,
  lowThreshold,
  highThreshold
}) => {
  let alertScore = getInDecimal(score);
  return (
    <div style={{ display: 'inline-flex' }}>
      <div style={{ width: '80px', marginRight: '8px' }}>
        <Progress
          percent={normalizeAlertScore(alertScore)}
          size="small"
          strokeWidth="12px"
          showInfo={false}
          strokeColor={getColor(riskRating)}
        />
      </div>
      <Tooltip title={score}>
        <div title={normalizeAlertScore(score)}>
          {riskRating || ''} {normalizeAlertScore(alertScore)}{' '}
          <span className={styles.greyed}> / 100</span>
        </div>
      </Tooltip>
    </div>
  );
};

export const ScoringPanel = ({
  data = {},
  authentication,
  lowThreshold,
  highThreshold,
  dispatch,
  getRiskScoreHistoryResponse,
  updateRiskScoreHistoryResponse,
  acceptRiskScoreResponse,
  isEmbedded
}) => {
  const [changeScoreModalVisible, setChangeScoreModalVisible] = useState(false);
  const [acceptScoreModalVisible, setAcceptScoreModalVisible] = useState(false);
  const [scoreHistoryVisible, setScoreHistoryVisible] = useState(false);
  const [acceptScoreDisabled, setAcceptScoreDisabled] = useState(false);
  const [newRiskLevel, setNewRiskLevel] = useState('');
  const [reason, setReason] = useState('');

  const ScoringPanelConfig =
    data.customerType === 'C'
      ? ScoringPanelCorporateConfig
      : ScoringPanelIndividualConfig;

  const handleChangeScoreModalApply = () => {
    dispatch(
      updateCRSRiskScoreHistory(
        {
          customerKey: data.customer_id,
          changeFrom: data.riskRating,
          changeTo: newRiskLevel,
          reason
        },
        GET_DATA
      )
    );
  };

  const handleAcceptScoreModalApply = () => {
    dispatch(
      putRiskScoreAcceptance(
        {
          customerKey: data.customer_id,
          riskRating: data.riskRating,
          notes: reason
        },
        GET_DATA
      )
    );
  };

  const handleChangeScoreModalCancel = () => {
    setChangeScoreModalVisible(false);
    setNewRiskLevel('');
    setReason('');
  };

  const handleAcceptScoreModalCancel = () => {
    setAcceptScoreModalVisible(false);
    setReason('');
  };

  const onRiskLevelChange = (e) => {
    setNewRiskLevel(e.target.value);
  };

  const onKeyDown = useCallback((event) => {
    if (event.keyCode === 27) {
      // when esc is pressed
      setScoreHistoryVisible(false);
    }
  }, []);

  const renderData = (key) => {
    if (key === 'customerRiskScore') {
      const score = data[key];
      return (
        <ScoreBar
          score={score}
          riskRating={data['riskRating']}
          lowThreshold={lowThreshold}
          highThreshold={highThreshold}
        />
      );
    } else if (key === 'riskScoreHistory') {
      return (
        <Badge
          size="small"
          count={getRiskScoreHistoryResponse?.data.count}
          offset={[-8, 0]}
          style={{ backgroundColor: '#21B4BD' }}>
          <Popover
            placement="bottomLeft"
            title={
              <>
                History of Risk Score Changes
                <Button
                  size="small"
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => setScoreHistoryVisible(false)}
                />
              </>
            }
            content={<ScoreHistoryTable customerKey={data.customer_id} />}
            trigger="click"
            visible={scoreHistoryVisible}
            onVisibleChange={(visible) => setScoreHistoryVisible(visible)}>
            <span className={styles.historyIcon} />
          </Popover>
        </Badge>
      );
    } else if (key === 'customerOnboardDate') {
      return convertYearsToCamelCase(moment(data[key]).fromNow(true));
    } else if (key === 'segmentCode') {
      const { segmentName = 'NA' } =
        find(segmentMapping, {
          segmentCode: data[key]
        }) || {};
      return segmentName;
    } else if (data[key]) {
      return data[key];
    }

    return '-';
  };

  useEffect(() => {
    if (updateRiskScoreHistoryResponse?.fetched) {
      setChangeScoreModalVisible(false);
      setNewRiskLevel('');
      setReason('');
      data.riskRating = newRiskLevel;
    }
  }, [updateRiskScoreHistoryResponse]);

  useEffect(() => {
    if (acceptRiskScoreResponse?.fetched) {
      setAcceptScoreModalVisible(false);
      setReason('');
      setAcceptScoreDisabled(true);
    }
  }, [acceptRiskScoreResponse]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, []);

  return (
    <>
      <div className={styles.actionHeader}>
        {!isEmbedded && <h3>Customer Details</h3>}
        {!isEmbedded &&
          hasResource(
            authentication?.data,
            crsResources.APP_3_RESOURCE_UI_CRS_CHANGE_OR_ACCEPT_RISK_SCORE
          ) && (
            <Button onClick={() => setChangeScoreModalVisible(true)}>
              Change Risk Score
            </Button>
          )}
        {!isEmbedded &&
          hasResource(
            authentication?.data,
            crsResources.APP_3_RESOURCE_UI_CRS_CHANGE_OR_ACCEPT_RISK_SCORE
          ) && (
            <Button
              type="primary"
              disabled={data['isReviewed'] || acceptScoreDisabled}
              onClick={() => setAcceptScoreModalVisible(true)}>
              Accept Risk Score
            </Button>
          )}
        <Modal
          className={styles.modal}
          style={{ top: 203 }}
          title="Change Risk Score"
          visible={changeScoreModalVisible}
          okText="Apply"
          onOk={handleChangeScoreModalApply}
          okButtonProps={{
            disabled: !isValidReason(reason) || !newRiskLevel,
            loading: updateRiskScoreHistoryResponse?.isFetching
          }}
          onCancel={handleChangeScoreModalCancel}>
          <div className={styles.scoreRow}>
            <div>
              <div className={styles.label}>Current Customer Risk Score</div>
              <ScoreBar
                score={getInDecimal(data['customerRiskScore'])}
                riskRating={data['riskRating']}
                lowThreshold={lowThreshold}
                highThreshold={highThreshold}
              />
            </div>
            <div>
              <div className={styles.label}>New Customer Risk Score</div>
              <Radio.Group
                size="small"
                value={newRiskLevel}
                onChange={onRiskLevelChange}>
                <Radio.Button
                  value="High"
                  disabled={data.riskRating?.toLowerCase() === 'high'}>
                  <div
                    className={styles.importanceIndicator}
                    style={{ background: '#DF6057' }}
                  />
                  High
                </Radio.Button>
                <Radio.Button
                  value="Medium"
                  disabled={data.riskRating?.toLowerCase() === 'medium'}>
                  <div
                    className={styles.importanceIndicator}
                    style={{ background: '#E29840' }}
                  />
                  Mid
                </Radio.Button>
                <Radio.Button
                  value="Low"
                  disabled={data.riskRating?.toLowerCase() === 'low'}>
                  <div
                    className={styles.importanceIndicator}
                    style={{ background: '#9AD692' }}
                  />
                  Low
                </Radio.Button>
              </Radio.Group>
            </div>
          </div>
          <div>
            <div className={styles.label}>Reasons for Change</div>
            <Input.TextArea
              rows="5"
              placeholder="Reasons for Changing Risk Score"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </Modal>
        <Modal
          className={styles.modal}
          style={{ top: 203 }}
          title="Accept Risk Score"
          visible={acceptScoreModalVisible}
          okText="Apply"
          onOk={handleAcceptScoreModalApply}
          okButtonProps={{
            disabled: !isValidReason(reason),
            loading: acceptRiskScoreResponse?.isFetching
          }}
          onCancel={handleAcceptScoreModalCancel}>
          <div>
            <div className={styles.label}>Reasons for Accept</div>
            <Input.TextArea
              rows="5"
              placeholder="Reasons for Accepting Risk Score"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </Modal>
      </div>
      <div className={styles.itemsContainer}>
        <div className={styles.item}>
          <div className={styles.userIcon}>
            <em className="fa fa-user" />
          </div>
        </div>
        {ScoringPanelConfig.map((col) => {
          return (
            <div className={styles.item} key={col.item}>
              <div className={styles.label}>{col.label}</div>
              <div className={styles.value}>{renderData(col.item, data)}</div>
            </div>
          );
        })}
      </div>
    </>
  );
};

function mapStateToProps(state, ownProps = {}) {
  const {
    updateRiskScoreHistoryResponse,
    getRiskScoreHistoryResponse,
    acceptRiskScoreResponse
  } = state.crsRiskDetailReducer;
  return {
    updateRiskScoreHistoryResponse,
    getRiskScoreHistoryResponse,
    acceptRiskScoreResponse,
    authentication: state.authentication
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ScoringPanel);
